import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { CameraTile } from '@vgps/livekit_player';

// Build-time tunable: defer 'recovering' overlay. undefined → CameraTile default.
const RECOVERING_OVERLAY_DELAY_MS = (() => {
  const v = Number(process.env.REACT_APP_LIVEKIT_RECOVERING_OVERLAY_MS);
  return Number.isFinite(v) && v >= 0 ? v : undefined;
})();

/**
 * StreamCameraTile — wrapper around the SDK's CameraTile that adds a hover
 * toolbar with mute / screenshot / fullscreen, mirroring the TrackingCameraTile
 * pattern used in the tracking map view.
 *
 * State (mediaState) comes from CameraTile's onMediaStateChange — no DOM
 * polling, no MutationObserver. Actions go through the CameraTileHandle ref
 * (setMuted / downloadScreenshot / requestFullscreen / exitFullscreen).
 *
 * The toolbar is positioned at the bottom-center of the tile and only appears
 * when the user hovers the tile AND the stream is live (state in playing /
 * buffering). The button surface is intentionally hit-tested to stop the
 * outer drag-and-drop swap handler from firing when the user clicks a button.
 */
function ToolButton({ onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.stopPropagation()}
      aria-label={label}
      title={label}
      style={{
        width: 34,
        height: 34,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: '50%',
        background: 'rgba(8,19,32,0.78)',
        color: '#fff',
        cursor: 'pointer',
        padding: 0,
        backdropFilter: 'blur(4px)',
      }}
    >
      {children}
    </button>
  );
}

function StreamCameraToolbar({
  isVisible,
  isMuted,
  isFullscreen,
  onToggleMute,
  onScreenshot,
  onToggleFullscreen,
}) {
  // Render even when hidden so the opacity transition has both states.
  // pointer-events follows visibility so the toolbar never intercepts clicks
  // (e.g. drag-to-swap) while it's faded out.
  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 56,
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        borderRadius: 999,
        background: 'rgba(8,19,32,0.45)',
        backdropFilter: 'blur(6px)',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: 'opacity 0.2s ease',
        zIndex: 12,
      }}
    >
      <ToolButton onClick={onToggleMute} label={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}>
        {isMuted ? <VolumeOffIcon style={{ fontSize: 20 }} /> : <VolumeUpIcon style={{ fontSize: 20 }} />}
      </ToolButton>
      <ToolButton onClick={onScreenshot} label="Chụp ảnh">
        <PhotoCameraIcon style={{ fontSize: 20 }} />
      </ToolButton>
      <ToolButton onClick={onToggleFullscreen} label={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}>
        {isFullscreen ? <FullscreenExitIcon style={{ fontSize: 20 }} /> : <FullscreenIcon style={{ fontSize: 20 }} />}
      </ToolButton>
    </div>
  );
}

export default function StreamCameraTile({ deviceSerial, channelId, title, height }) {
  const tileHandleRef = useRef(null);
  const containerRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFullscreenHovered, setIsFullscreenHovered] = useState(false);
  const fsTimeoutRef = useRef(null);
  const [mediaState, setMediaState] = useState({
    isLive: false,
    muted: true,
    volume: 0,
    isFullscreen: false,
    quality: 'sd',
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (containerRef.current) {
      setPortalTarget(containerRef.current.firstChild);
    }
  }, []);

  useEffect(() => {
    if (!mediaState.isFullscreen) {
      setIsFullscreenHovered(false);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
        fsTimeoutRef.current = null;
      }
      return;
    }

    const handleMouseMove = () => {
      setIsFullscreenHovered(true);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
      fsTimeoutRef.current = setTimeout(() => {
        setIsFullscreenHovered(false);
      }, 2500);
    };

    const handleMouseLeave = () => {
      setIsFullscreenHovered(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    handleMouseMove();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
    };
  }, [mediaState.isFullscreen]);

  const handleMediaStateChange = useCallback((next) => {
    setMediaState(next);
  }, []);

  const handleToggleMute = useCallback((event) => {
    event.stopPropagation();
    tileHandleRef.current?.setMuted(!mediaState.muted);
  }, [mediaState.muted]);

  const handleScreenshot = useCallback(async (event) => {
    event.stopPropagation();
    await tileHandleRef.current?.downloadScreenshot();
  }, []);

  const handleToggleFullscreen = useCallback(async (event) => {
    event.stopPropagation();
    const handle = tileHandleRef.current;
    if (!handle) return;
    if (mediaState.isFullscreen) {
      await handle.exitFullscreen();
    } else {
      await handle.requestFullscreen();
    }
  }, [mediaState.isFullscreen]);

  const toolbar = (
    <StreamCameraToolbar
      isVisible={mediaState.isLive && (mediaState.isFullscreen ? isFullscreenHovered : isHovered)}
      isMuted={mediaState.muted}
      isFullscreen={mediaState.isFullscreen}
      onToggleMute={handleToggleMute}
      onScreenshot={handleScreenshot}
      onToggleFullscreen={handleToggleFullscreen}
    />
  );

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <CameraTile
        ref={tileHandleRef}
        deviceSerial={deviceSerial}
        channelId={channelId}
        title={title}
        height={height}
        onMediaStateChange={handleMediaStateChange}
        recoveringOverlayDelayMs={RECOVERING_OVERLAY_DELAY_MS}
      />
      {portalTarget ? ReactDOM.createPortal(toolbar, portalTarget) : toolbar}
    </div>
  );
}
